export function kioskBase(appName: string): string {
  return `/kiosk/${appName}`;
}

export function parseRestaurantAppFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/restaurants\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export function isEmbeddedKioskWebView(): boolean {
  return typeof window !== 'undefined' && typeof window.PepparKiosk?.getConfig === 'function';
}

export function kioskLoginPath(appName: string): string {
  return `${kioskBase(appName)}/login`;
}

export function kioskOrderTypePath(appName: string): string {
  return `${kioskBase(appName)}/order-type`;
}

export function kioskMenuPath(appName: string, itemUrl?: string): string {
  if (itemUrl) {
    return `${kioskBase(appName)}/menu/${itemUrl}`;
  }
  return `${kioskBase(appName)}/menu`;
}

export function kioskCartPath(appName: string): string {
  return `${kioskBase(appName)}/cart`;
}

export function kioskConfirmationPath(appName: string): string {
  return `${kioskBase(appName)}/confirmation`;
}

export function kioskOrdersPath(appName: string, orderId?: string | number): string {
  if (orderId != null) {
    return `${kioskBase(appName)}/orders/${orderId}`;
  }
  return `${kioskBase(appName)}/orders`;
}

/** Map `/restaurants/:appName/...` to the matching `/kiosk/:appName/...` route. */
export function mapRestaurantPathToKiosk(appName: string, restaurantsPath: string): string {
  const prefix = `/restaurants/${appName}`;
  if (!restaurantsPath.startsWith(prefix)) {
    return restaurantsPath;
  }
  const rest = restaurantsPath.slice(prefix.length);
  const orderMatch = rest.match(/^\/orders\/([^/?#]+)/);
  if (orderMatch?.[1]) {
    const query = restaurantsPath.includes('?') ? restaurantsPath.split('?')[1] : '';
    const payment = new URLSearchParams(query).get('payment')?.toUpperCase();
    if (payment === 'SUCCESS' || payment === 'PAID') {
      const q = new URLSearchParams(query);
      q.set('orderId', orderMatch[1]);
      return `${kioskConfirmationPath(appName)}?${q.toString()}`;
    }
    return kioskOrdersPath(appName, orderMatch[1]) + (query ? `?${query}` : '');
  }
  if (rest === '/orders' || rest.startsWith('/my-orders')) {
    return kioskOrdersPath(appName);
  }
  const menuItemMatch = rest.match(/^\/menu\/([^/?#]+)/);
  if (menuItemMatch?.[1]) {
    return kioskMenuPath(appName, menuItemMatch[1]);
  }
  if (rest === '/menu' || rest.startsWith('/menu?')) {
    return kioskMenuPath(appName);
  }
  if (rest === '/cart' || rest.startsWith('/cart?')) {
    return kioskCartPath(appName);
  }
  return `${kioskBase(appName)}${rest.split('?')[0]}`;
}

export const KIOSK_ROLE_STORAGE_KEY = 'peppar.kiosk.role';
export const KIOSK_SESSION_STORAGE_KEY = 'peppar.kiosk.sessionId';
export const KIOSK_TABLE_STORAGE_KEY = 'peppar.kiosk.tableId';
export const KIOSK_TABLE_NUMBER_STORAGE_KEY = 'peppar.kiosk.tableNumber';
export const KIOSK_ORDER_TYPES_STORAGE_KEY = 'peppar.kiosk.orderTypes';
export const KIOSK_APP_STORAGE_KEY = 'peppar.kiosk.appName';
export const KIOSK_RESTRO_ID_STORAGE_KEY = 'peppar.kiosk.restroId';

export function persistKioskRestroId(restroId: number | string | null | undefined): void {
  if (restroId == null || restroId === '') {
    return;
  }
  try {
    sessionStorage.setItem(KIOSK_RESTRO_ID_STORAGE_KEY, String(restroId));
  } catch {
    /* ignore */
  }
}

export function readStoredKioskRestroId(): number | null {
  try {
    const raw =
      sessionStorage.getItem(KIOSK_RESTRO_ID_STORAGE_KEY) ||
      (typeof window !== 'undefined' ? window.PepparKiosk?.getConfig?.()?.restaurant?.id : null);
    if (raw == null || raw === '') {
      return null;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/** restroId for /api/payments — cart restaurant, kiosk device config, or session cache. */
export function resolveKioskPaymentRestroId(cartRestaurantId?: number | null): number | undefined {
  if (cartRestaurantId != null && Number.isFinite(cartRestaurantId)) {
    return cartRestaurantId;
  }
  const stored = readStoredKioskRestroId();
  return stored ?? undefined;
}

const KIOSK_QUEUE_TOKEN_PREFIX = 'peppar.kiosk.queueToken.';

export function persistKioskQueueToken(orderId: number | string, label: string): void {
  try {
    sessionStorage.setItem(`${KIOSK_QUEUE_TOKEN_PREFIX}${orderId}`, label);
  } catch {
    /* private mode / quota */
  }
}

/** True when kiosk checkout should open the payment gateway immediately after place-order. */
export function kioskMustPayOnlineAtCheckout(
  orderType: number,
  checkoutPayMethod: 'ONLINE_PAYMENT' | 'CASH_ON_DELIVERY',
  payFirst: boolean,
  payOnlineAvailable: boolean
): boolean {
  if (!payOnlineAvailable) {
    return false;
  }
  if (orderType === 2 || orderType === 3) {
    return checkoutPayMethod === 'ONLINE_PAYMENT';
  }
  return payFirst;
}

export function readKioskQueueToken(orderId: number | string | null | undefined): string | null {
  if (orderId == null || orderId === '') {
    return null;
  }
  try {
    return sessionStorage.getItem(`${KIOSK_QUEUE_TOKEN_PREFIX}${orderId}`);
  } catch {
    return null;
  }
}

/** Table dine-in: stay on order feedback, then end kiosk session. */
export const KIOSK_TABLE_DINEIN_FEEDBACK_LOGOUT_MS = 5 * 60 * 1000;

export function readStoredKioskAppName(): string | null {
  try {
    return sessionStorage.getItem(KIOSK_APP_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function hasActiveKioskSession(): boolean {
  try {
    return !!sessionStorage.getItem(KIOSK_SESSION_STORAGE_KEY);
  } catch {
    return false;
  }
}

/** Guest ordering URLs that have a kiosk equivalent (`/restaurants/:app/...`). */
export function isRestaurantPathRedirectableToKiosk(pathname: string): boolean {
  return /^\/restaurants\/[^/]+(?:\/(?:menu|cart|my-orders|orders)(?:\/|$)|\/?$)/.test(pathname);
}

export function shouldRedirectRestaurantPathToKiosk(appName: string): boolean {
  if (typeof window !== 'undefined' && window.PepparKiosk?.getConfig) {
    return true;
  }
  if (!hasActiveKioskSession()) {
    return false;
  }
  const storedApp = readStoredKioskAppName();
  return !storedApp || storedApp === appName;
}
